import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import Flex from '@/components/common/Flex';
import IconItem from '@/components/common/icon/IconItem';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useAppContext } from '@/Main';

/** تم‌های روشن که روی پس‌زمینه primary آیکون سفید دیده نمی‌شود */
const LIGHT_PRIMARY_VARIANTS = new Set(['primary', 'yellow']);

const useNeedsDarkOnPrimary = () => {
  const {
    config: { themeVariant }
  } = useAppContext() as { config: { themeVariant?: string } };
  return LIGHT_PRIMARY_VARIANTS.has(themeVariant || '');
};

export function OverviewSectionTitle({
  title,
  subtitle,
  icon,
  helpHtml,
  action,
  className = 'mb-3 mt-1'
}: {
  title: string;
  subtitle?: ReactNode;
  icon: IconProp;
  helpHtml: string;
  action?: ReactNode;
  className?: string;
}) {
  const needsDarkOnPrimary = useNeedsDarkOnPrimary();

  return (
    <Flex className={`align-items-start justify-content-between gap-3 ${className}`}>
      <Flex className="align-items-start gap-2">
        <IconItem
          tag="div"
          icon={icon}
          bg="primary"
          color={needsDarkOnPrimary ? 'dark' : 'white'}
          size="sm"
          iconClass={needsDarkOnPrimary ? 'fs--1' : 'fs--2'}
          className="shadow-none flex-shrink-0"
        />
        <div className="d-flex flex-column">
          <h5 className="mb-0 text-700 lh-base">
            <span className="d-inline-flex align-items-center gap-1">
              {title}
              <HelpTooltip html={helpHtml} />
            </span>
          </h5>
          {subtitle ? <p className="mb-0 fs--2 text-500">{subtitle}</p> : null}
        </div>
      </Flex>
      {action ? <div className="flex-shrink-0">{action}</div> : null}
    </Flex>
  );
}

export function OverviewCardTitle({
  icon,
  title,
  helpHtml
}: {
  icon: IconProp;
  title: string;
  helpHtml: string;
}) {
  const needsDarkOnPrimary = useNeedsDarkOnPrimary();

  return (
    <span className="d-inline-flex align-items-center gap-2">
      <FontAwesomeIcon
        icon={icon}
        className={needsDarkOnPrimary ? 'text-dark' : 'text-primary'}
        style={needsDarkOnPrimary ? { fontSize: '1.05em' } : undefined}
      />
      <span className="d-inline-flex align-items-center">
        {title}
        <HelpTooltip placement="top" html={helpHtml} />
      </span>
    </span>
  );
}

export function OverviewItemTitle({
  icon,
  title,
  helpHtml,
  iconClassName = 'text-500'
}: {
  icon: IconProp;
  title: string;
  helpHtml: string;
  iconClassName?: string;
}) {
  return (
    <span className="d-inline-flex align-items-center gap-1 min-w-0">
      <FontAwesomeIcon
        icon={icon}
        className={`flex-shrink-0 ${iconClassName}`}
      />
      <span className="text-truncate">{title}</span>
      <HelpTooltip placement="top" html={helpHtml} />
    </span>
  );
}
